import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import {
  formatEUR,
  formatDateFR,
  FrenchTaxCalculator,
  type TaxSummary,
  type TaxableEvent
} from '../../services/frenchTaxCalculator';

interface FormGeneratorProps {
  summary: TaxSummary | null;
  events: TaxableEvent[];
  year: number;
}

// Extract exchange accounts from events
function extractExchangeAccounts(events: TaxableEvent[]) {
  const exchangeMap = new Map<string, { firstDate: string; lastDate: string }>();

  const exchangeCountries: Record<string, string> = {
    'Binance': 'Îles Caïmans',
    'Coinbase': 'États-Unis',
    'Kraken': 'États-Unis',
    'Crypto.com': 'Singapour',
    'KuCoin': 'Seychelles',
    'Bitget': 'Seychelles',
    'OKX': 'Seychelles',
    'Bybit': 'Îles Vierges britanniques',
    'Gate.io': 'Îles Caïmans',
    'Huobi': 'Seychelles',
    'Bitstamp': 'Luxembourg',
    'Gemini': 'États-Unis',
  };

  for (const event of events) {
    const exchange = event.exchange || 'Unknown';
    if (exchange === 'Manual' || exchange === 'Unknown') continue;

    const existing = exchangeMap.get(exchange);
    if (existing) {
      if (new Date(event.date) < new Date(existing.firstDate)) {
        existing.firstDate = event.date;
      }
      if (new Date(event.date) > new Date(existing.lastDate)) {
        existing.lastDate = event.date;
      }
    } else {
      exchangeMap.set(exchange, { firstDate: event.date, lastDate: event.date });
    }
  }

  return Array.from(exchangeMap.entries()).map(([name, dates]) => ({
    name,
    country: exchangeCountries[name] || 'Inconnu',
    openDate: dates.firstDate,
    isActive: true,
  }));
}

export function FormGenerator({ summary, events, year }: FormGeneratorProps) {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState<string | null>(null);

  // Generate Form 2086 data
  const form2086Data = summary
    ? new FrenchTaxCalculator([]).generateForm2086Data(summary)
    : null;

  // Generate Form 3916-bis data
  const form3916Data = new FrenchTaxCalculator([]).generateForm3916BisData(events);

  // Extract exchange accounts
  const exchangeAccounts = extractExchangeAccounts(events);

  // Export to CSV
  const exportToCSV = (filename: string, headers: string[], rows: string[][]) => {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Generate Form 2086 CSV
  const handleExport2086CSV = async () => {
    if (!form2086Data) return;

    setGenerating('2086-csv');
    await new Promise(resolve => setTimeout(resolve, 300));

    const headers = [
      'Date de cession',
      'Actif numérique',
      'Prix de cession (€)',
      'Prix d\'acquisition (€)',
      'Plus-value ou moins-value (€)'
    ];

    const rows = form2086Data.cessions.map(c => [
      formatDateFR(c.date),
      c.asset,
      c.prixCession.toFixed(2),
      c.prixAcquisition.toFixed(2),
      c.plusValue.toFixed(2)
    ]);

    rows.push([]);
    rows.push(['TOTAUX', '', '', '', '']);
    rows.push(['Total plus-values', '', '', '', form2086Data.totals.totalPlusValues.toFixed(2)]);
    rows.push(['Total moins-values', '', '', '', (-form2086Data.totals.totalMoinsValues).toFixed(2)]);
    rows.push(['Plus-value nette', '', '', '', form2086Data.totals.plusValueNette.toFixed(2)]);
    rows.push(['Montant imposable', '', '', '', form2086Data.totals.montantImposable.toFixed(2)]);

    exportToCSV(`formulaire_2086_${year}.csv`, headers, rows);
    setGenerating(null);
  };

  // Generate Form 3916-bis CSV
  const handleExport3916CSV = async () => {
    setGenerating('3916-csv');
    await new Promise(resolve => setTimeout(resolve, 300));

    const headers = [
      'Nom de la plateforme',
      'Pays',
      'Date d\'ouverture',
      'Date de fermeture',
      'Type de compte'
    ];

    const rows = form3916Data.accounts.map(a => [
      a.exchange,
      a.country,
      formatDateFR(a.openedDate),
      a.closedDate ? formatDateFR(a.closedDate) : 'En cours',
      a.accountType === 'custodial' ? 'Custodial (CEX)' : 'Non-custodial'
    ]);

    exportToCSV(`formulaire_3916-bis_${year}.csv`, headers, rows);
    setGenerating(null);
  };

  // Generate Tax Certificate CSV
  const handleExportCertificate = async () => {
    if (!summary) return;

    setGenerating('certificate');
    await new Promise(resolve => setTimeout(resolve, 300));

    const headers = ['Élément', 'Valeur'];
    const rows = [
      ['Année fiscale', summary.year.toString()],
      ['Nombre de transactions analysées', events.length.toString()],
      ['Nombre de cessions imposables', summary.totalSales.toString()],
      ['Total des cessions', formatEUR(summary.totalSaleValue)],
      ['Plus-values brutes', formatEUR(summary.totalCapitalGains)],
      ['Moins-values brutes', formatEUR(Math.abs(summary.totalCapitalLosses))],
      ['Plus-value nette', formatEUR(summary.netCapitalGain)],
      ['Seuil d\'exonération', formatEUR(summary.exemptionThreshold)],
      ['Statut', summary.isExempt ? 'EXONÉRÉ' : 'IMPOSABLE'],
      ['Montant imposable', formatEUR(summary.taxableAmount)],
      ['PFU à payer (30%)', formatEUR(summary.flatTax)],
    ];

    exportToCSV(`certificat_fiscal_${year}.csv`, headers, rows);
    setGenerating(null);
  };

  // Generate Grand Livre CSV
  const handleExportLedger = async () => {
    setGenerating('ledger');
    await new Promise(resolve => setTimeout(resolve, 300));

    const headers = [
      'Date',
      'Type',
      'Actif',
      'Quantité',
      'Prix unitaire (€)',
      'Valeur totale (€)',
      'Frais (€)',
      'Plateforme',
      'Notes'
    ];

    const typeLabels: Record<string, string> = {
      purchase: 'Achat',
      sale: 'Vente',
      staking_reward: 'Récompense staking',
      mining: 'Minage',
      airdrop: 'Airdrop',
      swap_in: 'Swap entrant',
      swap_out: 'Swap sortant',
    };

    const rows = events.map(e => [
      formatDateFR(e.date),
      typeLabels[e.type] || e.type,
      e.asset,
      e.quantity.toString(),
      e.pricePerUnit.toFixed(2),
      e.totalValue.toFixed(2),
      (e.fees || 0).toFixed(2),
      e.exchange || '',
      e.notes || ''
    ]);

    exportToCSV(`grand_livre_transactions_${year}.csv`, headers, rows);
    setGenerating(null);
  };

  if (!summary) {
    return (
      <div style={{
        padding: '48px',
        textAlign: 'center',
      }}>
        <Icons.Download size={48} style={{ color: 'var(--muted)', marginBottom: '16px' }} />
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
          {t('taxCenter.forms.noData', 'Calculate Taxes First')}
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
          {t('taxCenter.forms.calculateFirst', 'You need to calculate your taxes before generating forms')}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Info Banner */}
      <div style={{
        padding: '16px',
        background: 'rgba(59, 130, 246, 0.1)',
        borderRadius: '12px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
      }}>
        <Icons.AlertTriangle size={20} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#3b82f6', marginBottom: '4px' }}>
            {t('taxCenter.forms.disclaimer', 'Important')}
          </h4>
          <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: '1.5' }}>
            {t('taxCenter.forms.disclaimerText', 'These documents are generated for informational purposes. Please verify all data and consult a tax professional before filing. French tax laws may change - always refer to official sources.')}
          </p>
        </div>
      </div>

      {/* Form Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
      }}>
        {/* Form 2086 */}
        <div style={{
          padding: '24px',
          background: 'var(--hover)',
          borderRadius: '16px',
          border: '1px solid var(--border)',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
          }}>
            <Icons.FileText size={24} style={{ color: 'white' }} />
          </div>

          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
            {t('taxCenter.forms.form2086', 'Form 2086')}
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.5' }}>
            {t('taxCenter.forms.form2086Desc', 'Capital gains declaration for digital assets. Lists all taxable disposals with calculated gains/losses.')}
          </p>

          <div style={{
            padding: '12px',
            background: 'var(--card)',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--muted)' }}>{t('taxCenter.forms.sales', 'Sales')}</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{summary.totalSales}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--muted)' }}>{t('taxCenter.forms.netGain', 'Net Gain')}</span>
              <span style={{
                fontWeight: 600,
                color: summary.netCapitalGain >= 0 ? '#10b981' : '#ef4444',
              }}>
                {formatEUR(summary.netCapitalGain)}
              </span>
            </div>
          </div>

          <button
            onClick={handleExport2086CSV}
            disabled={generating !== null}
            className="btn-primary"
            style={{ width: '100%', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            {generating === '2086-csv' ? (
              <Icons.RefreshCw size={18} className="animate-spin" />
            ) : (
              <Icons.FileText size={18} />
            )}
            {t('taxCenter.forms.downloadCSV', 'Download CSV')}
          </button>
        </div>

        {/* Form 3916-bis */}
        <div style={{
          padding: '24px',
          background: 'var(--hover)',
          borderRadius: '16px',
          border: '1px solid var(--border)',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
          }}>
            <Icons.Globe size={24} style={{ color: 'white' }} />
          </div>

          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
            {t('taxCenter.forms.form3916', 'Form 3916-bis')}
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.5' }}>
            {t('taxCenter.forms.form3916Desc', 'Declaration of foreign digital asset accounts. Lists all exchanges where you have accounts.')}
          </p>

          <div style={{
            padding: '12px',
            background: 'var(--card)',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--muted)' }}>{t('taxCenter.forms.accounts', 'Accounts')}</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{exchangeAccounts.length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--muted)' }}>{t('taxCenter.forms.platforms', 'Platforms')}</span>
              <span style={{ color: 'var(--text)', fontWeight: 500, fontSize: '12px' }}>
                {exchangeAccounts.map(a => a.name).join(', ').substring(0, 25) || 'N/A'}
                {exchangeAccounts.length > 2 ? '...' : ''}
              </span>
            </div>
          </div>

          <button
            onClick={handleExport3916CSV}
            disabled={generating !== null}
            className="btn-primary"
            style={{ width: '100%', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          >
            {generating === '3916-csv' ? (
              <Icons.RefreshCw size={18} className="animate-spin" />
            ) : (
              <Icons.FileText size={18} />
            )}
            {t('taxCenter.forms.downloadCSV', 'Download CSV')}
          </button>
        </div>

        {/* Tax Certificate */}
        <div style={{
          padding: '24px',
          background: 'var(--hover)',
          borderRadius: '16px',
          border: '2px solid #8b5cf6',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Icons.Shield size={24} style={{ color: 'white' }} />
            </div>
          </div>

          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
            {t('taxCenter.forms.certificate', 'Tax Certificate')}
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.5' }}>
            {t('taxCenter.forms.certificateDesc', 'Summary of tax calculation conforming to Article 150 VH bis CGI. Includes all key figures.')}
          </p>

          <div style={{
            padding: '12px',
            background: 'var(--card)',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--muted)' }}>{t('taxCenter.forms.taxableAmount', 'Taxable')}</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatEUR(summary.taxableAmount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--muted)' }}>{t('taxCenter.forms.pfu', 'PFU 30%')}</span>
              <span style={{ fontWeight: 600, color: '#8b5cf6' }}>
                {formatEUR(summary.flatTax)}
              </span>
            </div>
          </div>

          <button
            onClick={handleExportCertificate}
            disabled={generating !== null}
            className="btn-primary"
            style={{
              width: '100%',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
            }}
          >
            {generating === 'certificate' ? (
              <Icons.RefreshCw size={18} className="animate-spin" />
            ) : (
              <Icons.FileText size={18} />
            )}
            {t('taxCenter.forms.downloadCSV', 'Download CSV')}
          </button>
        </div>

        {/* Grand Livre */}
        <div style={{
          padding: '24px',
          background: 'var(--hover)',
          borderRadius: '16px',
          border: '2px solid #f59e0b',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Icons.BookOpen size={24} style={{ color: 'white' }} />
            </div>
          </div>

          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
            {t('taxCenter.forms.ledger', 'Grand Livre')}
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px', lineHeight: '1.5' }}>
            {t('taxCenter.forms.ledgerDesc', 'Complete transaction history for audit purposes. Includes all purchases, sales, and rewards.')}
          </p>

          <div style={{
            padding: '12px',
            background: 'var(--card)',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--muted)' }}>{t('taxCenter.forms.transactions', 'Transactions')}</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{events.length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--muted)' }}>{t('taxCenter.forms.period', 'Period')}</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{year}</span>
            </div>
          </div>

          <button
            onClick={handleExportLedger}
            disabled={generating !== null}
            className="btn-primary"
            style={{ width: '100%', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
          >
            {generating === 'ledger' ? (
              <Icons.RefreshCw size={18} className="animate-spin" />
            ) : (
              <Icons.FileText size={18} />
            )}
            {t('taxCenter.forms.downloadCSV', 'Download CSV')}
          </button>
        </div>
      </div>

      {/* Account List Preview */}
      {exchangeAccounts.length > 0 && (
        <div style={{
          marginTop: '24px',
          padding: '20px',
          background: 'var(--hover)',
          borderRadius: '12px',
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '16px' }}>
            {t('taxCenter.forms.accountsToDecl', 'Accounts to Declare (3916-bis)')}
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {exchangeAccounts.map((account, idx) => (
              <div
                key={idx}
                style={{
                  padding: '8px 16px',
                  background: 'var(--card)',
                  borderRadius: '8px',
                  fontSize: '13px',
                }}
              >
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>{account.name}</span>
                <span style={{ color: 'var(--muted)', marginLeft: '8px' }}>({account.country})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
